import React, { useState } from 'react';
import { FinancialsHeader, CustomersContainer } from './styles';
import NewConstructionRedirect from '../NewConstructionRedirect';
import moment from 'moment';
import {
  IFabricationContract,
  EFabricationContractsStatus
} from '../../../../store/ducks/fabrication-contracts/types';
import { DateField } from '@gerdau/hefesto-mais-web/DateField';
import { DateRangeType } from '@gerdau/hefesto-mais-web/DateField/DateField.types';
import { Button, Select, SearchField, Title } from '@gerdau/hefesto';
import { useDispatch, useSelector } from 'react-redux';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ApplicationState } from '../../../../store';

interface IContractsAndConstructionsFiltersProps {
  customerContracts: IFabricationContract[];
  setFilteredContracts(filteredContracts: IFabricationContract[]): void;
  onCleanFilters(): void;
  isAllDisabled: boolean;
}

const ContractsAndConstructionsFilters: React.FC<
  IContractsAndConstructionsFiltersProps
> = ({
  customerContracts,
  setFilteredContracts,
  onCleanFilters,
  isAllDisabled
}) => {
  const contractsManager = useSelector(
    (state: ApplicationState) =>
      state.contractsAndConstructions.contractsManager
  );

  const [startSelectedDate, setStartSelectedDate] =
    useState<moment.Moment | null>(null);

  const [endSelectedDate, setEndSelectedDate] = useState<moment.Moment | null>(
    null
  );
  const [searchInput, setSearchInput] = useState('');

  const [dateRange, setDateRange] = useState<DateRangeType>([null, null]);

  const [selectedStatus, setSelectedStatus] =
    useState<EFabricationContractsStatus>(EFabricationContractsStatus.ALL);

  const onFilter = (
    newStartDate: moment.Moment | null,
    newEndDate: moment.Moment | null,
    newStatusValue: EFabricationContractsStatus,
    newSearchInputValue: string
  ) => {
    const handleFilterByStartDate = (
      newStartDate: moment.Moment | null,
      filteredContracts: IFabricationContract[]
    ) => {
      return filteredContracts.filter((construction) => {
        const creationDate = moment(construction.creationDate, 'YYYY-MM-DD');
        return creationDate.isSameOrAfter(newStartDate);
      });
    };

    const handleFilterByEndDate = (
      newEndDate: moment.Moment | null,
      filteredContracts: IFabricationContract[]
    ) => {
      return filteredContracts.filter((construction) => {
        const creationDate = moment(construction.creationDate, 'YYYY-MM-DD');
        return creationDate.isSameOrBefore(newEndDate);
      });
    };

    const handleFilterByStatus = (
      newStatusValue: EFabricationContractsStatus,
      filteredContracts: IFabricationContract[]
    ) => {
      return filteredContracts.filter((construction) => {
        return construction.status === newStatusValue;
      });
    };

    const handleFilterBySearchInput = (
      newSearchInputValue: string,
      filteredContracts: IFabricationContract[]
    ) => {
      return filteredContracts.filter((construction) => {
        return (
          construction.contractNumber
            .toUpperCase()
            .includes(newSearchInputValue) ||
          construction.soldTo.taxNumber
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.customerOrder
            .toUpperCase()
            .includes(newSearchInputValue) ||
          construction.shipTo.taxNumber
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.shipTo.taxState
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.shipTo.address
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.shipTo.state
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.shipTo.city
            ?.toUpperCase()
            .includes(newSearchInputValue) ||
          construction.logistic?.responsiblesOfConstruction.some(
            (responsible) =>
              responsible.name.toUpperCase().includes(newSearchInputValue)
          ) ||
          construction.logistic?.responsiblesOfConstruction.some(
            (responsible) =>
              responsible.email.toUpperCase().includes(newSearchInputValue)
          )
        );
      });
    };
    let newFilteredContracts = customerContracts;

    setSearchInput(newSearchInputValue);
    newFilteredContracts = handleFilterBySearchInput(
      newSearchInputValue.toUpperCase(),
      newFilteredContracts
    );

    if (newStatusValue !== EFabricationContractsStatus.ALL) {
      setSelectedStatus(newStatusValue);
      newFilteredContracts = handleFilterByStatus(
        newStatusValue,
        newFilteredContracts
      );
    }

    if (newStartDate !== null) {
      setStartSelectedDate(newStartDate);
      newFilteredContracts = handleFilterByStartDate(
        newStartDate,
        newFilteredContracts
      );
    }

    if (newEndDate !== null) {
      setEndSelectedDate(newEndDate);
      newFilteredContracts = handleFilterByEndDate(
        newEndDate,
        newFilteredContracts
      );
    }

    setFilteredContracts(newFilteredContracts);
  };

  const statusSelectOptions = Object.values(EFabricationContractsStatus).map(
    (status) => ({ value: status, label: status })
  );

  const cleanFilters = () => {
    setStartSelectedDate(null);
    setEndSelectedDate(null);
    setDateRange([null, null]);
    setSelectedStatus(EFabricationContractsStatus.ALL);
    setSearchInput('');
    onCleanFilters();
  };

  const dispatch = useDispatch();

  const clearFiltersTagging = () => {
    const codEmr = contractsManager.selectedCustomerId
      ? contractsManager.selectedCustomerId
      : 'not_set';

    const stt = selectedStatus ?? 'not_set';

    const ftl = searchInput ?? 'not_set';

    const dtInicio = startSelectedDate
      ? moment(startSelectedDate).format('YYYY/MM/DD')
      : 'not_set';
    const dtFim = endSelectedDate
      ? moment(endSelectedDate).format('YYYY/MM/DD')
      : 'not_set';

    dispatch(
      sendTagging({
        screenName: 'LG:ContratosEObras:SelecaoDoEmissor',
        event: 'click',
        itemName: 'button: limpar_filtros',
        itemDescription: 'contratoEObras_selecaoDoEmissor_limparFiltros',
        filterInfo: `#codEmr:${codEmr}#dtInicio:${dtInicio}#dtFim:${dtFim}#stt:${stt}#flt:${ftl}`
      })
    );
  };

  return (
    <FinancialsHeader>
      <Title level={3}>Filtros</Title>
      <NewConstructionRedirect
        customerContracts={customerContracts.filter(
          (contract) =>
            contract.status === EFabricationContractsStatus.NEW_CONTRACT
        )}
        disabled={
          isAllDisabled ||
          customerContracts.filter(
            (contract) =>
              contract.status === EFabricationContractsStatus.NEW_CONTRACT
          ).length === 0
        }
      />
      <CustomersContainer>
        <div className="input-periodo">
          <DateField
            label="Período"
            name="dateField"
            selectsRange={true}
            onChange={(date: DateRangeType) => {
              setDateRange(date);
              onFilter(
                moment(date?.[0]),
                moment(date?.[1]),
                selectedStatus,
                searchInput
              );
            }}
            disabled={isAllDisabled}
            data-testid="dateRange"
            range={dateRange}
          />
        </div>

        <div className="input-status">
          <Select
            label={'Status'}
            disabled={isAllDisabled}
            name="statusSelect"
            options={statusSelectOptions}
            placeholder={`- Selecione -`}
            value={selectedStatus}
            onSelect={(value) =>
              onFilter(
                startSelectedDate,
                endSelectedDate,
                value as EFabricationContractsStatus,
                searchInput
              )
            }
          />
        </div>

        <div className="input-search">
          <SearchField
            data-testid="hefesto-search-field"
            label="Pesquisar"
            name="search"
            inputValue={searchInput}
            disabled={isAllDisabled}
            onChange={(e) => {
              onFilter(
                startSelectedDate,
                endSelectedDate,
                selectedStatus,
                e.target.value
              );
            }}
            helperText="Pesquise por número de contrato, nome da obra ou responsável pela obra."
            placeholder="Pesquisar"
          />
        </div>

        <Button
          name="limparFiltros"
          variant="none"
          onClick={() => {
            cleanFilters();
            clearFiltersTagging();
          }}
          disabled={isAllDisabled}
          loading={isAllDisabled}
        >
          Limpar filtros
        </Button>
      </CustomersContainer>
    </FinancialsHeader>
  );
};

export default ContractsAndConstructionsFilters;
